import { FC, forwardRef } from "react";

import { Theme, useTheme, SxProps } from "@mui/material/styles";

import { mergeSx } from "@/common/helpers/mergeSx";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton/DashboardButton";
import { DashboardButtonProps } from "@/dashboard-design-system/DashboardButton/types";
import { DashboardIcon, DashboardIconProps } from "@/dashboard-design-system/Icon";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton";
import { IconElement } from "@/icons/types";

export const iconButtonSizeOptions = ["extraSmall", "small", "medium", "large"] as const;
export type IconButtonSize = (typeof iconButtonSizeOptions)[number];

export type DashboardIconButtonProps = Omit<
  DashboardButtonProps,
  "fullWidth" | "startIcon" | "endIcon" | "children" | "size" | "slotProps"
> & {
  children: IconElement;
  size?: IconButtonSize;
  slotProps?: {
    icon?: {
      sx?: SxProps<Theme>;
    };
  };
};

const ICON_BUTTON_SIZE_MAP: Record<IconButtonSize, (theme: Theme) => number> = Object.freeze({
  extraSmall: () => 24,
  small: (theme: Theme) => theme.button.size.default.small,
  medium: (theme: Theme) => theme.button.size.default.medium,
  large: (theme: Theme) => theme.button.size.default.large,
});

const ICON_SIZE_MAP: Record<IconButtonSize, DashboardIconProps["size"]> = Object.freeze({
  extraSmall: "small",
  small: "medium",
  medium: "medium",
  large: "medium",
});

const ICON_BUTTON_TO_BUTTON_SIZE_MAP: Record<IconButtonSize, DashboardButtonProps["size"]> = Object.freeze({
  extraSmall: "small",
  small: "small",
  medium: "medium",
  large: "large",
});

export const DashboardIconButton = forwardRef<HTMLButtonElement, DashboardIconButtonProps>(
  ({ children, size = "medium", sx, slotProps, ...rest }, ref) => {
    const theme = useTheme();

    const styles = mergeSx(
      {
        // Reset button styles
        padding: 0,
        minHeight: 0,
        lineHeight: "100%",
        width: ICON_BUTTON_SIZE_MAP[size](theme),
        height: ICON_BUTTON_SIZE_MAP[size](theme),
        ...(size === "extraSmall" && {
          borderRadius: 0.75,
        }),
      },
      sx
    );

    return (
      <DashboardButton ref={ref} sx={styles} size={ICON_BUTTON_TO_BUTTON_SIZE_MAP[size]} {...rest}>
        <DashboardIcon vibe="currentColor" size={ICON_SIZE_MAP[size]} sx={slotProps?.icon?.sx}>
          {children}
        </DashboardIcon>
      </DashboardButton>
    );
  }
);

DashboardIconButton.displayName = "DashboardIconButton";

type DashboardIconButtonSkeletonProps = {
  size?: IconButtonSize;
  sx?: SxProps<Theme>;
};

export const DashboardIconButtonSkeleton: FC<DashboardIconButtonSkeletonProps> = ({
  size: sizeProp = "medium",
  sx,
}) => {
  const theme = useTheme();
  const size = ICON_BUTTON_SIZE_MAP[sizeProp](theme);

  const styles = mergeSx(
    {
      width: size,
      height: size,
      borderRadius: sizeProp === "extraSmall" ? 0.75 : 1,
    },
    sx
  );

  return <DashboardSkeleton variant="rectangular" sx={styles} />;
};
